/** The money the operator approves against, as the server stores it (integer cents). */
export type PnL = {
  list_revenue_cents: number
  lost_to_discount_cents: number
  net_revenue_cents: number
  costs: Record<string, number>
  total_cost_cents: number
  net_result_cents: number
  /** Shown below the line; never part of the net result. */
  possible_lifetime_cents: number
}

export type PnlRow = { label: string; cents: number; below?: boolean }

const MINUS = '−'

/** "$1,234.56"; a negative amount reads "−$12.00". */
export function formatCents(n: number): string {
  const abs = Math.abs(Math.round(n))
  const dollars = Math.floor(abs / 100).toLocaleString('en-US')
  const cents = String(abs % 100).padStart(2, '0')
  return `${n < 0 ? MINUS : ''}$${dollars}.${cents}`
}

const COST_LABELS: Record<string, string> = { email: 'Email', ai: 'AI' }

/** "promo_print" → "Promo print" for a cost kind with no label of its own. */
export function costLabel(kind: string): string {
  if (COST_LABELS[kind]) return COST_LABELS[kind]
  const words = kind.replace(/_/g, ' ').trim()
  return words.charAt(0).toUpperCase() + words.slice(1)
}

/** The P&L as table rows, top to bottom; possible lifetime value sits below the line. */
export function pnlRows(p: PnL): PnlRow[] {
  return [
    { label: 'List revenue', cents: p.list_revenue_cents },
    { label: 'Lost to discounting', cents: p.lost_to_discount_cents },
    { label: 'Net revenue', cents: p.net_revenue_cents },
    ...Object.entries(p.costs ?? {}).map(([kind, cents]) => ({ label: costLabel(kind), cents })),
    { label: 'Total costs', cents: p.total_cost_cents },
    { label: 'Net result', cents: p.net_result_cents },
    { label: 'Possible lifetime value', cents: p.possible_lifetime_cents, below: true },
  ]
}

export type SideRow = { label: string; expected: number | null; actual: number | null; below?: boolean }

/** Expected next to actual for the results table. Cost kinds from either side each get a row;
 *  a figure one side never had is null (the expected P&L may be missing on an old campaign). */
export function sideBySide(expected: Partial<PnL>, actual: Partial<PnL>): SideRow[] {
  const kinds = [...new Set([...Object.keys(expected.costs ?? {}), ...Object.keys(actual.costs ?? {})])]
  const pick = (p: Partial<PnL>, key: keyof PnL): number | null => {
    const v = p[key]
    return typeof v === 'number' ? v : null
  }
  const cost = (p: Partial<PnL>, k: string): number | null => (p.costs && k in p.costs ? p.costs[k] : null)
  const row = (label: string, key: keyof PnL, below?: boolean): SideRow =>
    ({ label, expected: pick(expected, key), actual: pick(actual, key), below })
  return [
    row('List revenue', 'list_revenue_cents'),
    row('Lost to discounting', 'lost_to_discount_cents'),
    row('Net revenue', 'net_revenue_cents'),
    ...kinds.map((k) => ({ label: costLabel(k), expected: cost(expected, k), actual: cost(actual, k), below: undefined })),
    row('Total costs', 'total_cost_cents'),
    row('Net result', 'net_result_cents'),
    row('Possible lifetime value', 'possible_lifetime_cents', true),
  ]
}

/** Dollars typed by the operator ("12", "12.5", "$1,200") as cents; null when it isn't an amount. */
export function parseDollars(s: string): number | null {
  const t = s.replace(/[$,\s]/g, '')
  if (!/^\d+(\.\d{0,2})?$/.test(t)) return null
  return Math.round(Number(t) * 100)
}
